import {FileUploadModalProps} from "./FileUploadModalProps";
import React, {ReactElement, useEffect, useRef, useState} from "react";
import {Body, Footer, FooterItem, Header} from "@zendeskgarden/react-modals";
import {Field, FileUpload, Input, Label, Message} from "@zendeskgarden/react-forms";
import {Button} from "@zendeskgarden/react-buttons";
import {Dots, Inline} from "@zendeskgarden/react-loaders";
import {COLORS} from "../../../../constants/Colors";
import {
    AcquisitionPropertyTemplateName,
    useCheckExternalCoaByNameLazyQuery,
    useCreateAcquisitionExternalCoaMutation,
    useGenerateAcquisitionExternalCoaFileUploadUrlMutation
} from "../../../../__generated__/generated_types";
import {toast} from "react-toastify";
import {FileUploadModalStates} from "./FileUploadModalStates";
import {useFetchTemplateCsv} from "../../logic/useFetchTemplateCsv";
import {CreateCustomCoAButton} from "./csvbox/CreateCustomCoAButton";
import {CustomCoADataItem} from "./csvbox/custom-coa-common";

export function CreateCustomCoaModal(props: FileUploadModalProps): ReactElement {

    const [coaName, setCoaName] = useState<string>("");
    const [nameAvailable, setNameAvailable] = useState<boolean>(true);
    const [coaData, setCoaData] = useState<CustomCoADataItem[] | undefined>(undefined);
    const [coaFileName, setCoaFileName] = useState<string>("");
    const [isUploading, setIsUploading] = useState<boolean>(false);

    const [generateUrl, {data: urlData, loading: loadingUrl}] = useGenerateAcquisitionExternalCoaFileUploadUrlMutation({
        notifyOnNetworkStatusChange: true
    });
    const [processFile, {loading: loadingProcess}] = useCreateAcquisitionExternalCoaMutation({
        notifyOnNetworkStatusChange: true
    });
    const [checkName, {data: dataCheckName, loading: loadingCheckName}] = useCheckExternalCoaByNameLazyQuery({
        fetchPolicy: "no-cache"
    });

    const {fetchTemplate} = useFetchTemplateCsv("custom-coa");

    useEffect(() => {
        if(urlData && !loadingUrl && isUploading) {
            uploadFiles();
        }
    }, [urlData, loadingUrl]);

    useEffect(() => {
        if(!coaName || coaName === "") {
            setNameAvailable(true);
            return;
        }

        const timeout = setTimeout(() => {
            checkName({
                variables: {
                    name: coaName
                }
            });
            setNameAvailable(true);
        }, 500);

        return () => clearTimeout(timeout);
    }, [coaName]);

    useEffect(() => {
        if(loadingCheckName || !dataCheckName) {
            return;
        }

        setNameAvailable(!dataCheckName.getExternalChartOfAccountsByName);
    }, [loadingCheckName, dataCheckName]);


    const reset = () => {
        setCoaName("");
        setCoaData(undefined);
        props.onModalClose();
    };

    const uploadFiles = () => {
        if(!urlData || !coaData) {
            setIsUploading(false);
            return;
        }

        // Get URLs and keys
        const coaUrl = urlData.generateExternalChartOfAccountsFileUploadUrl.signedUrl;
        const coaFileKey = urlData.generateExternalChartOfAccountsFileUploadUrl.key;

        const encoder = new TextEncoder();
        const encoded = encoder.encode(JSON.stringify(coaData));

        // Create array of upload promises
        fetch(coaUrl, {
            method: 'PUT',
            body: encoded.buffer,
            headers: {
                'Content-Type': "application/json",
            }
        })
        .then(() => {
            return processFile({
                variables: {
                    name: coaName,
                    fileKey: coaFileKey,
                    propertyId: props.propertyId
                }
            });
        })
        .then(res => {
            if(res.data?.createExternalChartOfAccountsAcquisitionProperty) {
                toast.success("Created new chart of accounts.");
                // This allows users to process data.
                props.setState(FileUploadModalStates.CHOOSE_EXTERNAL_COA);
            }
        })
        .catch((error) => {
            console.error("Error uploading file:", error);
            toast.error("Error uploading file");
        })
        .finally(() => {
            setIsUploading(false);
        });
    };

    const handleSubmit = () => {
        if(!coaName) {
            toast.error("Please enter a chart of accounts name.");
            return;
        }

        if (!coaData) {
            toast.error("Please select a chart of accounts file.");
            return;
        }

        setIsUploading(true);
        generateUrl();
    };

    return <>
        <Header>Create Custom Chart of Accounts</Header>
        <Body>
            <form>
                <Field style={{marginBottom: "1.5rem"}}>
                    <Label>Chart of Accounts Name</Label>
                    <Input type="text" placeholder="Name" value={coaName} onChange={(event) => setCoaName(event.target.value)} />
                    {!nameAvailable &&
                        <>
                            <Message validation="error">Name already exists.</Message>
                            <div style={{fontSize: "0.75rem", marginTop: "0.5em"}}>Go to <Button isLink onClick={() => props.setState(FileUploadModalStates.CHOOSE_EXTERNAL_COA)}>update account values</Button> to use or change.</div>
                        </>
                    }
                    {loadingCheckName && <div style={{fontSize: "0.75rem", marginTop: "0.5em"}}>Checking Name <Dots /></div>}
                </Field>
                <div>
                    <h5 style={{fontWeight: "bold"}}>Chart of Accounts File with Values</h5>
                    <CreateCustomCoAButton
                        checkingName={loadingCheckName}
                        data={(filename, uploaded) =>  {
                            setCoaData(uploaded);
                            setCoaFileName(filename);
                        }}
                    />
                    {coaData &&
                        <div style={{fontSize: "0.9rem", marginTop: "0.25rem"}}>
                            {coaFileName}
                            <Message style={{marginLeft: "0.25rem"}}>You can change the file by clicking the button above.</Message>
                        </div>
                    }
                </div>
                <div style={{marginTop: "0.75rem"}}><Button isLink onClick={() => fetchTemplate(props.propertyId, AcquisitionPropertyTemplateName.ExternalCoa)}>Download Custom CoA Template</Button></div>
            </form>
        </Body>
        <Footer>
            <FooterItem>
                <Button onClick={reset} isBasic disabled={isUploading || loadingUrl || loadingProcess}>
                    Cancel
                </Button>
            </FooterItem>
            <FooterItem>
                <Button isPrimary onClick={handleSubmit} disabled={!coaData || isUploading || loadingUrl || loadingProcess || loadingCheckName || !nameAvailable}>
                    {
                        (isUploading || loadingUrl || loadingProcess)
                                ? <Inline size={24} color={COLORS.POSITIVE_COLOR} aria-label="loading"/>
                                : <span>Create</span>
                    }
                </Button>
            </FooterItem>
        </Footer>
    </>;
}